import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadProgress,
  mergeProgress,
  recordAttempt,
  recordClear,
  recordSkip,
  restoreFromMirrors,
  sameProgress,
  saveProgress,
  type ClearEffort,
  type PlausibilityFlag,
  type Progress,
} from '../progress';
import type { AccountUser } from '../lib/account';
import { ProgressSynchronizer, type SyncState } from '../lib/progress-sync';
export type { SyncState } from '../lib/progress-sync';
import { exportCode, importCode } from '../lib/backup';
import { askToPersist } from '../lib/vault';
import type { PublicMission } from '../types';
import type { ClearResult } from '../components/contracts';

/**
 * Player progress, held in a ref and mirrored into state for rendering.
 *
 * The functions in progress.ts mutate the object and write to localStorage,
 * which must happen exactly once per event. Putting that inside a setState
 * updater would double-count XP under React's development double-invocation,
 * so the mutation happens here against a ref and the render copy is published
 * afterwards.
 *
 * ProgressSynchronizer owns account requests and serializes saves. Normal
 * records merge; a different account or a newer reset epoch replaces the
 * local record. The page renders local changes immediately and retries a
 * failed sync on the next change or foreground reconciliation.
 *
 * Generation checks invalidate asynchronous mirror and backup reads after
 * account changes and any other moment the record is replaced rather than
 * merged. Network requests have their own session and abort guard in the
 * synchronizer.
 */


/** What came of a pasted backup code. `reason` is a finished sentence, from lib/backup's catalogue, shown as it is. */
export type RestoreResult = { ok: true; signed: boolean; changed: boolean } | { ok: false; reason: string };

export interface ProgressApi {
  progress: Progress;
  /** `flags` are the plausibility flags from the passing grade; omitted means none were reported. */
  clear(mission: PublicMission, hintsUsed: number, flags?: PlausibilityFlag[], effort?: ClearEffort): ClearResult;
  attempt(missionId: string): void;
  /** The learner moved past a level without clearing it. Earns nothing; opens the next one. */
  skip(missionId: string): void;
  /** Whether the record is also on the account, and how that is going. */
  sync: SyncState;
  /** This learner's record as one line of text they can keep. */
  backup(): Promise<string>;
  /** A pasted code, merged in. Never replaces what this browser already has. */
  restore(code: string): Promise<RestoreResult>;
}



/**
 * The shortest gap between two pulls of the account's copy.
 *
 * The pull on returning to the tab is there to catch a second device having
 * moved, which is a thing that happens between sessions, not between glances.
 * Without a floor, every alt-tab back into the game is a Worker request and a
 * D1 read for a record that is almost always the one already in hand — and on
 * the free plan those are a budget, not a rounding error. A minute is far
 * below the interval at which a learner actually switches devices and far
 * above the interval at which they switch windows.
 */
const RECONCILE_MIN_MS = 60_000;

export function useProgress(user: AccountUser | null): ProgressApi {
  const ref = useRef<Progress | null>(null);
  if (ref.current === null) ref.current = loadProgress();
  const [progress, setProgress] = useState<Progress>(ref.current);
  const [sync, setSync] = useState<SyncState>('off');

  const generation = useRef(0);
  const synchronizer = useRef<ProgressSynchronizer | null>(null);
  if (synchronizer.current === null) synchronizer.current = new ProgressSynchronizer({
    read: () => ref.current!,
    write: (value, replaced) => {
      if (replaced) generation.current += 1;
      ref.current = value;
      saveProgress(value);
      setProgress(value);
    },
    status: setSync,
  });
  const userId = user?.id ?? null;
  useEffect(() => {
    generation.current += 1;
    const syncer = synchronizer.current!;
    syncer.connect(userId);
    let lastPull = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastPull < RECONCILE_MIN_MS) return;
      lastPull = Date.now();
      syncer.reconcile();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { syncer.disconnect(); document.removeEventListener('visibilitychange', onVisible); };
  }, [userId]);

  const publish = useCallback(() => {
    const p = ref.current!;
    // `struggle` is carried through and stays optional. A record that has
    // never cleared a tagged level has none, and publishing `undefined` is
    // what the components below should read — `struggledWith` answers null on
    // it, which is "no memory" rather than "no struggle".
    setProgress({
      version: 2,
      xp: p.xp,
      levels: { ...p.levels },
      lastClear: p.lastClear,
      ...(p.struggle ? { struggle: p.struggle } : {}),
    });
  }, []);

  const push = useCallback(() => synchronizer.current!.changed(), []);

  /**
   * Merge a record in and keep it, unless the record has been replaced
   * since `gen` was taken. Returns whether anything actually changed, which
   * is what decides both whether to re-render and whether the account needs
   * telling.
   */
  const absorb = useCallback(
    (incoming: Progress, gen: number): boolean => {
      if (gen !== generation.current) return false;
      const merged = mergeProgress(ref.current!, incoming);
      if (sameProgress(merged, ref.current!)) return false;
      ref.current = merged;
      saveProgress(merged);
      publish();
      return true;
    },
    [publish],
  );

  /**
   * What the browser's other stores kept. Runs once, after the first render,
   * and almost always finds either nothing or exactly what localStorage
   * already had — in which case `absorb` changes nothing and nobody notices.
   * The case it exists for is the browser that lost its localStorage without
   * being asked to: cleared on its own, evicted for space, or expired after a
   * week away. There, this is the whole of the recovery.
   */
  useEffect(() => {
    const gen = generation.current;
    void restoreFromMirrors().then((recovered) => {
      if (recovered && absorb(recovered, gen)) push();
    });
  }, [absorb, push]);

  /**
   * Another tab in this browser wrote the record. Without this, two open tabs
   * each hold their own copy and the last one to write wins, quietly losing
   * whatever the other had done. A removal is this browser's storage being
   * cleared out from under the tab, and is followed rather than merged: there
   * is nothing left to merge with, and the mirrors are what recovers it.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && !e.key.startsWith('engineeros.progress.')) return;
      if (e.newValue === null) {
        generation.current += 1;
        ref.current = loadProgress();
        publish();
        return;
      }
      const gen = generation.current;
      // Read through loadProgress rather than parsing the event's value, so
      // the signature check and the v1 migration are the same ones as ever.
      absorb(loadProgress(), gen);
      synchronizer.current!.reconcile();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [absorb, publish]);

  const clear = useCallback(
    (mission: PublicMission, hintsUsed: number, flags: PlausibilityFlag[] = [], effort: ClearEffort = {}): ClearResult => {
      const result = recordClear(ref.current!, mission, hintsUsed, flags, effort);
      publish();
      push();
      // A person who has cleared a level has something worth keeping, which is
      // the moment to ask the browser not to evict it. Asked here rather than
      // on load because in the browsers where this is a visible permission
      // prompt, a prompt on arrival is noise and a prompt now is a question
      // with an obvious answer.
      void askToPersist();
      return result;
    },
    [publish, push],
  );

  const attempt = useCallback(
    (missionId: string) => {
      recordAttempt(ref.current!, missionId);
      publish();
      push();
    },
    [publish, push],
  );

  const skip = useCallback(
    (missionId: string) => {
      recordSkip(ref.current!, missionId);
      publish();
      push();
    },
    [publish, push],
  );

  const backup = useCallback(() => exportCode(ref.current!), []);

  const restore = useCallback(
    async (code: string): Promise<RestoreResult> => {
      const gen = generation.current;
      const result = await importCode(code);
      if (!result.ok) return result;
      const changed = absorb(result.progress, gen);
      if (changed) push();
      return { ok: true, signed: result.signed, changed };
    },
    [absorb, push],
  );

  return { progress, clear, attempt, skip, sync, backup, restore };
}
